import { Heading1, Heading2 } from 'components/shared/Typography';
import styled from 'styled-components';
import {
  color,
  container,
  fontFamily,
  fontSize,
  fontWeight,
  radius,
  screen,
  spacing
} from 'theme';
import { getPublicPath } from 'utils/paths';

export const StyledContainer = styled.div`
  max-width: ${container('xl')};
  padding: ${spacing('xl5')} ${spacing('md')};
  margin: auto;
`;

export const StyledBillboard = styled.div`
  background-image: ${() =>
    `url('${getPublicPath(
      '/images/stateOfHiring/polygons.svg'
    )}'), linear-gradient(135deg, #005153 0%, #28cebc 100%)`};
  background-repeat: no-repeat;
  background-position: top right;
  background-size: cover;
  color: ${color('white')};
  font-size: ${fontSize('lg')};
  line-height: 1.6;
  text-align: center;

  @media (min-width: ${screen('md')}) {
    text-align: left;
    font-size: ${fontSize('xl2')};
    line-height: 1.58;
  }

  p {
    margin-bottom: 0;
  }

  ${StyledContainer} {
    padding-top: ${spacing('xl4')};
    padding-bottom: ${spacing('xl4')};

    @media (min-width: ${screen('md')}) {
      padding-top: ${spacing('xl6')};
      padding-bottom: ${spacing('xl6')};
    }
  }
`;

export const StyledBillboardContent = styled.div`
  max-width: 42rem;
`;

export const StyledBillboardTitle = styled(Heading1)`
  font-size: ${fontSize('xl5')};

  @media (min-width: ${screen('md')}) {
    font-size: ${fontSize('xl6')};
  }

  @media (min-width: ${screen('lg')}) {
    font-size: ${fontSize('xl7')};
  }
`;

export const StyledHeader = styled.div`
  text-align: center;
  max-width: ${container('md')};
  margin: auto;
  font-size: ${fontSize('lg')};
  line-height: 1.6;
`;

export const StyledApplicationsContent = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing('xl2')};

  @media (min-width: ${screen('md')}) {
    grid-template-columns: 1fr 1fr;
  }

  ${Heading2} {
    margin-top: 0;
  }

  > div {
    display: flex;
    flex-direction: column;
    gap: ${spacing('xl')};
  }
`;

export const StyledConversionsContent = styled.div`
  max-width: ${container('lg')};
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing('xl2')};
  margin: ${spacing('xl3')} auto 0;
  line-height: 1.5;

  @media (min-width: ${screen('md')}) {
    grid-template-columns: 1fr 1fr;
    font-size: ${fontSize('lg')};
    line-height: 1.6;
  }

  p {
    margin-top: 0;
  }
`;

export const StyledAvailability = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing('lg')};
  margin-top: ${spacing('xl3')};

  figure {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: ${spacing('lg')};
  }
`;

export const StyledAvailabilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, auto);
  gap: ${spacing('md')};

  @media (min-width: ${screen('md')}) {
    grid-template-columns: repeat(10, 1.5rem);
  }

  > div {
    height: ${spacing('sm')};
    width: ${spacing('sm')};

    @media (min-width: ${screen('md')}) {
      height: ${spacing('lg')};
      width: ${spacing('lg')};
    }

    &.available {
      background-color: ${color('mint_900')};
    }

    &.open {
      background-color: ${color('sun_700')};
    }

    &.unavailable {
      background-color: ${color('purple_700')};
    }
  }
`;

export const StyledLegend = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing('xs')};
  font-size: ${fontSize('md')};
  margin-bottom: ${spacing('xs')};

  > div {
    width: ${spacing('sm')};
    height: ${spacing('sm')};
    border-radius: ${radius('full')};

    &.available {
      background-color: ${color('mint_900')};
    }

    &.open {
      background-color: ${color('sun_700')};
    }

    &.unavailable {
      background-color: ${color('purple_700')};
    }
  }
`;

export const StyledCard = styled.div`
  background-color: ${color('sand_100')};
  padding: ${spacing('xl2')} ${spacing('xl')};
  text-align: center;
  display: flex;
  flex-direction: column;
  border-radius: ${radius('lg')};
`;

export const StyledCardTitle = styled.h4`
  margin: 0;
  font-size: ${fontSize('md')};
  line-height: 1.25;
  text-transform: uppercase;
  font-weight: ${fontWeight('extraBold')};
  color: ${color('text_subtle')};
`;

export const StyledValue = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  color: ${color('navy_500')};
  font-size: ${fontSize('lg')};

  &::after {
    content: '';
    width: ${spacing('xl4')};
    height: ${spacing('xs')};
    background-color: ${color('navy_200')};
    position: absolute;
    bottom: -${spacing('sm')};
    left: 50%;
    transform: translateX(-50%);
  }
`;

export const StyledProgress = styled.div`
  background-color: ${color('emerald_100')};
  padding: ${spacing('xl')} ${spacing('lg')};
  display: flex;
  flex-direction: column;
  gap: ${spacing('sm')};
  font-weight: ${fontWeight('medium')};
  line-height: 1.5;
  font-size: ${fontSize('md')};

  &:first-of-type {
    margin-top: ${spacing('lg')};
  }
  &:nth-of-type(2) {
    background-color: ${color('emerald_150')};
  }
  &:nth-of-type(3) {
    background-color: ${color('emerald_200')};
  }
  &:nth-of-type(4) {
    background-color: ${color('emerald_300')};
  }
`;

export const StyledProgressValue = styled.div`
  font-family: ${fontFamily('display')};
  font-size: ${fontSize('xl5')};
  line-height: 1;
  font-weight: ${fontWeight('semiBold')};
  text-transform: uppercase;
  color: ${color('emerald_700')};
`;
